import React, { useRef, useState } from "react";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Fab,
  Tabs,
  Tab,
  Button,
  Divider,
  Avatar,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  formatteDateDay,
  generateUniqueMatterName,
} from "../../../styles/styles";
import { LoadingButton } from "@mui/lab";
import { useDialog } from "../../DialogContext";

const BillPreview = ({ entryData, autoDownload = false }) => {
  const { closeDialog } = useDialog();
  const billContentRef = useRef(null);
  const [selectedLayout, setSelectedLayout] = useState(0);
  const [useExtra, setUseExtra] = useState(
    !!entryData?.full_data?.extra &&
      Object.keys(entryData.full_data.extra).length > 0
  );
  const theme = useTheme();

  React.useEffect(() => {
    if (autoDownload) {
      handleDownloadPDF();
    }
  }, [autoDownload]);

  const handleDownloadPDF = async () => {
    if (!billContentRef.current) return;

    try {
      const element = billContentRef.current;
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });
      const pageWidth = 210;
      const pageHeight = 297;
      const marginBottom = 10;
      const padding = 10;

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        scrollY: -window.scrollY,
      });

      const imgData = canvas.toDataURL("image/png");
      const imgWidth = pageWidth - 2 * padding;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let positionY = padding;
      let remainingHeight = imgHeight;
      let pageIndex = 0;

      while (remainingHeight > 0) {
        if (pageIndex > 0) pdf.addPage();
        const cropHeight = Math.min(
          remainingHeight,
          pageHeight - marginBottom - padding
        );
        pdf.addImage(
          imgData,
          "PNG",
          padding,
          positionY,
          imgWidth,
          cropHeight,
          undefined,
          "FAST"
        );

        pageIndex++;
        remainingHeight -= cropHeight;
        pdf.setFontSize(10);
        pdf.setTextColor(50);

        pdf.text(`Page ${pageIndex}`, pageWidth / 2, pageHeight - 5, {
          align: "center",
        });
      }

      pdf.save(`Invoice_${entryData?.invoice_num || "Bill"}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  const extra = entryData?.full_data?.extra || {};

  const InvoiceData = {
    firmData: {
      name: useExtra && extra.firm_name
        ? extra.firm_name
        : entryData?.full_data?.firm?.name,
      address: useExtra && extra.firm_address
        ? extra.firm_address
        : entryData?.full_data?.firm?.address,
      city: useExtra && extra.firm_city
        ? extra.firm_city
        : entryData?.full_data?.firm?.city,
      state: useExtra && extra.firm_state
        ? extra.firm_state
        : entryData?.full_data?.firm?.state,
      zip: useExtra && extra.firm_pincode
        ? extra.firm_pincode
        : entryData?.full_data?.firm?.pin_code,
    },
    clientData: {
      name:
        entryData?.full_data?.case?.case_related_person?.first_name ||
        entryData?.full_data?.case?.case_related_person?.last_name
          ? `${
              entryData?.full_data?.case?.case_related_person?.first_name || ""
            } ${
              entryData?.full_data?.case?.case_related_person?.last_name || ""
            }`.trim()
          : "--",
      address: useExtra && extra.client_address
        ? extra.client_address
        : entryData?.full_data?.case?.case_related_person?.address_1,
      city: useExtra && extra.client_city
        ? extra.client_city
        : entryData?.full_data?.case?.case_related_person?.city,
      state: useExtra && extra.client_state
        ? extra.client_state
        : entryData?.full_data?.case?.case_related_person?.state,
      zip: useExtra && extra.client_pincode
        ? extra.client_pincode
        : entryData?.full_data?.case?.case_related_person?.pincode,
      case: entryData?.full_data?.case?.id
        ? generateUniqueMatterName(
            entryData?.full_data?.case?.id,
            entryData?.full_data?.case?.case_name
          )
        : "--",
    },
    billData: {
      invoice_num: entryData?.invoice_num,
      issue_date: formatteDateDay(entryData?.full_data?.issue_date),
      due_date: formatteDateDay(entryData?.full_data?.due_date),
      total_amount: entryData?.full_data?.total_amount,
      status: entryData?.full_data?.status,
    },

    InvoiceAccountRow: [
      {
        invoice_num: entryData?.invoice_num || "--",
        amount_due: entryData?.full_data?.total_amount || "0.00",
        payment_get: entryData?.full_data?.payment_received || "0.00",
        issue_date: formatteDateDay(entryData?.full_data?.issue_date),
        due_date: formatteDateDay(entryData?.full_data?.due_date),
        balance_due: entryData?.full_data?.total_amount || "0.00",
      },
    ],
  };

  const columns = [
    { field: "type", headerName: "Type", width: "16%" },
    { field: "due_date", headerName: "Due Date", width: "16%" },
    { field: "quantity", headerName: "Qty", width: "16%" },
    { field: "rate", headerName: "Rate($)", width: "16%" },
    {
      field: "non_billable_amount",
      headerName: "Non-billable($)",
      width: "18%",
    },
    {
      field: "billable_amount",
      headerName: "Total Amount",
      width: "18%",
      align: "right",
    },
  ];

  // Process row data
  const allRows =
    entryData?.full_data?.expenses
      ?.filter((expense) => !expense?.non_billable || expense?.bill_entry_show)
      .map((expense, index) => ({
        id: index + 1,
        type: expense?.type || "--",
        due_date: formatteDateDay(entryData?.full_data?.due_date),
        quantity: expense?.quantity || 0,
        rate: expense?.hourly_rate || "0.00",
        non_billable_amount: expense?.non_billable
          ? expense.total_payment
          : "0.00",
        billable_amount: !expense?.non_billable
          ? expense.total_payment
          : "0.00",
      })) || [];

  // Calculate totals for the summary
  const calculateTotals = () => {
    if (!allRows.length)
      return { billableTotal: "0.00", nonBillableTotal: "0.00" };

    return allRows.reduce(
      (acc, row) => {
        const billable = parseFloat(row.billable_amount) || 0;
        const nonBillable = parseFloat(row.non_billable_amount) || 0;

        return {
          billableTotal: acc.billableTotal + billable,
          nonBillableTotal: acc.nonBillableTotal + nonBillable,
        };
      },
      { billableTotal: 0, nonBillableTotal: 0 }
    );
  };

  const { billableTotal, nonBillableTotal } = calculateTotals();

  const InvoiceAccount = [
    { field: "invoice_num", headerName: "Invoice Number", width: "20%" },
    { field: "due_date", headerName: "Due Date", width: "20%" },
    { field: "amount_due", headerName: "Amount Due", width: "20%" },
    { field: "payment_get", headerName: "Payment Received", width: "20%" },
    {
      field: "balance_due",
      headerName: "Balance Due",
      width: "20%",
      align: "right",
    },
  ];

  const renderSummaryTable = (hideHeader = false) => (
    <TableContainer sx={{ pageBreakInside: "avoid" }}>
      <Table sx={{ tableLayout: "fixed", width: "100%" }}>
        {!hideHeader && (
          <TableHead>
            <TableRow sx={{ pageBreakInside: "avoid" }}>
              {columns.map((col) => (
                <TableCell
                  key={col.field}
                  sx={{
                    fontWeight: 550,
                    fontSize: "0.8rem",
                    width: col.width,
                    padding: 1,
                    textAlign: col.align || "left",
                    backgroundColor: theme.palette.grey[200],
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                  align={col.align || "left"}
                >
                  {col.headerName}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
        )}
        <TableBody>
          {allRows.length > 0 ? (
            allRows.map((row, index) => (
              <TableRow key={index}>
                {columns.map((col) => (
                  <TableCell
                    key={col.field}
                    sx={{
                      fontSize: "0.7rem",
                      padding: 1,
                      width: col.width,
                      textAlign: col.align || "left",
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                    align={col.align || "left"}
                  >
                    {row[col.field] ?? "-"}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                align="center"
                sx={{ fontSize: "0.75rem", padding: 2 }}
              >
                No Data Available
              </TableCell>
            </TableRow>
          )}

          {allRows.length > 0 && (
            <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
              <TableCell
                colSpan={4}
                align="right"
                sx={{ fontWeight: 600, padding: 1 }}
              >
                Total:
              </TableCell>
              <TableCell align="left" sx={{ fontWeight: 600, padding: 1 }}>
                ${nonBillableTotal}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, padding: 1 }}>
                ${billableTotal}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderAccountTable = (hideHeader = false) => (
    <TableContainer sx={{ pageBreakInside: "avoid" }}>
      <Table
        sx={{
          tableLayout: "fixed",
          width: "100%",
          borderCollapse: "separate",
          borderSpacing: 0,
        }}
      >
        {!hideHeader && (
          <TableHead>
            <TableRow sx={{ pageBreakInside: "avoid" }}>
              {InvoiceAccount.map((col) => (
                <TableCell
                  key={col.field}
                  sx={{
                    fontWeight: 550,
                    fontSize: "0.8rem",
                    width: col.width,
                    padding: 1,
                    textAlign: col.align || "left",
                    backgroundColor: theme.palette.grey[200],
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                  align={col.align || "left"}
                >
                  {col.headerName}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
        )}
        <TableBody>
          {InvoiceData.InvoiceAccountRow.length > 0 ? (
            InvoiceData.InvoiceAccountRow.map((invoice, index) => (
              <TableRow key={index}>
                {InvoiceAccount.map((col) => (
                  <TableCell
                    key={col.field}
                    sx={{
                      fontSize: "0.7rem",
                      padding: 1,
                      width: col.width,
                      textAlign: col.align || "left",
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                    align={col.align || "left"}
                  >
                    {col.field === "amount_due" ||
                    col.field === "balance_due" ||
                    col.field === "payment_get"
                      ? `$${parseFloat(invoice[col.field] || 0).toFixed(2)}`
                      : invoice[col.field] ?? "-"}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={InvoiceAccount.length}
                align="center"
                sx={{ fontSize: "0.75rem", padding: 2 }}
              >
                No Invoice History Available
              </TableCell>
            </TableRow>
          )}
          {InvoiceData.InvoiceAccountRow.length > 0 && (
            <>
              <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                <TableCell
                  colSpan={InvoiceAccount.length - 1}
                  align="right"
                  sx={{ fontWeight: 600, padding: 1 }}
                >
                  Outstanding Balance:
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 600, padding: 1, textAlign: "right" }}
                >
                  $
                  {parseFloat(
                    InvoiceData?.InvoiceAccountRow[0]?.balance_due || 0
                  ).toFixed(2)}
                </TableCell>
              </TableRow>
              <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                <TableCell
                  colSpan={InvoiceAccount.length - 1}
                  align="right"
                  sx={{ fontWeight: 600, padding: 1 }}
                >
                  Total Amount Outstanding:
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 600, padding: 1, textAlign: "right" }}
                >
                  $
                  {parseFloat(
                    InvoiceData?.InvoiceAccountRow[0]?.balance_due || 0
                  ).toFixed(2)}
                </TableCell>
              </TableRow>
            </>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          mb: 2,
          position: "sticky",
          top: 0,
          bgcolor: theme.palette.background.paper,
          zIndex: 10,
        }}
      >
        <Tabs
          value={selectedLayout}
          onChange={(e, newValue) => setSelectedLayout(newValue)}
        >
          <Tab
            label="Layout 1 - Classic"
            sx={{ fontSize: "0.75rem", minHeight: 32 }}
          />
          <Tab
            label="Layout 2 - Modern"
            sx={{ fontSize: "0.75rem", minHeight: 32 }}
          />
          <Tab
            label="Layout 3 - With Logo"
            sx={{ fontSize: "0.75rem", minHeight: 32 }}
          />
          <Tab
            label="Layout 4 - Professional"
            sx={{ fontSize: "0.75rem", minHeight: 32 }}
          />
        </Tabs>
      </Box>

      {/* Content to be printed - Width set to match A4 aspect ratio */}
      <Box
        sx={{
          p: "1rem",
          bgcolor: theme.palette.background.default,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Card
          sx={{
            p: "3rem 2rem",
            width: "49.625rem",
            overflow: "hidden",
            maxWidth: "100%",
            margin: "0 auto",
            position: "relative",
            pageBreakInside: "avoid",
          }}
          ref={billContentRef}
        >
          {selectedLayout === 0 && (
            <>
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <Typography
                  variant="body1"
                  sx={{ fontSize: "1.3rem", fontWeight: 500 }}
                >
                  Law office of {InvoiceData.firmData.name}
                </Typography>
                <Typography variant="body1" sx={{ fontSize: "0.8rem" }}>
                  {InvoiceData.firmData.address}, {InvoiceData.firmData.city},
                </Typography>
                <Typography variant="body1" sx={{ fontSize: "0.8rem" }}>
                  {InvoiceData.firmData.state}-{InvoiceData.firmData.zip}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: "1.5rem",
                  flexWrap: { xs: "wrap", sm: "nowrap" },
                }}
              >
                <Box
                  sx={{
                    width: { xs: "100%", sm: "50%" },
                    mb: { xs: 2, sm: 0 },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ fontSize: "0.9rem", fontWeight: "500" }}
                  >
                    Bill To:
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontSize: "0.8rem", textTransform: "capitalize" }}
                  >
                    {InvoiceData.clientData.name}
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: "0.8rem" }}>
                    {InvoiceData.clientData.address
                      ? `${InvoiceData.clientData.address}, ${InvoiceData.clientData.city}`
                      : ""}
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: "0.8rem" }}>
                    {InvoiceData.clientData.state || ""}{" "}
                    {InvoiceData.clientData.zip || ""}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontSize: "0.8rem", fontWeight: "500", mt: 1 }}
                  >
                    Matter: {InvoiceData.clientData.case}
                  </Typography>
                </Box>
                <Box
                  sx={{ width: { xs: "100%", sm: "50%" }, textAlign: "right" }}
                >
                  <Typography sx={{ fontSize: "0.8rem", fontWeight: "500" }}>
                    Invoice No: {InvoiceData?.billData?.invoice_num}
                  </Typography>
                  <Typography sx={{ fontSize: "0.8rem", fontWeight: "500" }}>
                    Issue Date: {InvoiceData.billData.issue_date}
                  </Typography>
                  <Typography sx={{ fontSize: "0.8rem", fontWeight: "500" }}>
                    Due Date: {InvoiceData.billData.due_date}
                  </Typography>
                  <Typography
                    color={
                      InvoiceData.billData.status === "draft"
                        ? "info"
                        : InvoiceData.billData.status === "pending"
                        ? "warning"
                        : InvoiceData.billData.status === "unpaid"
                        ? "error"
                        : InvoiceData.billData.status === "paid"
                        ? "success"
                        : "primary"
                    }
                    sx={{
                      fontSize: "0.9rem",
                      fontWeight: "500",
                      textTransform: "capitalize",
                      mt: 1,
                    }}
                  >
                    Bill Status: {InvoiceData.billData.status}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}
              >
                {/* Summary of Invoice */}
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography
                    variant="body1"
                    sx={{ fontSize: "0.9rem", fontWeight: 600, mb: 1 }}
                  >
                    Summary of the invoice
                  </Typography>
                  {renderSummaryTable()}
                </Box>

                {/* Detailed Statement */}
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography
                    variant="body1"
                    sx={{ fontSize: "0.9rem", fontWeight: 600, mb: 1 }}
                  >
                    Detailed Statement of Account
                  </Typography>
                  {renderAccountTable()}
                </Box>
                {/* Footer - Positioned at the bottom of the page */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "2rem",
                    mt: "2rem",
                  }}
                >
                  <Box>
                    <Typography variant="body1" sx={{ fontSize: "0.8rem" }}>
                      Please pay within 30 days.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </>
          )}
          {selectedLayout === 1 && (
            <>
              <Box
                sx={{
                  bgcolor: theme.palette.primary.dark,
                  color: theme.palette.primary.contrastText,
                  p: 2,
                  mb: 3,
                  borderRadius: 1,
                  textAlign: "center",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                  INVOICE
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Law office of {InvoiceData.firmData.name}
                </Typography>
                <Typography variant="caption">
                  {InvoiceData.firmData.address}, {InvoiceData.firmData.city},
                </Typography>
                <Typography variant="caption">
                  {InvoiceData.firmData.state}-{InvoiceData.firmData.zip}
                </Typography>
              </Box>

              {/* Modern Grid Layout */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 3,
                  mb: 3,
                }}
              >
                <Card sx={{ p: 2, bgcolor: theme.palette.background.paper }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: theme.palette.primary.main,
                      mb: 1,
                      fontWeight: 600,
                    }}
                  >
                    Bill To
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 500, textTransform: "capitalize" }}
                  >
                    {InvoiceData.clientData.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {InvoiceData.clientData.address
                      ? `${InvoiceData.clientData.address}, ${InvoiceData.clientData.city}`
                      : ""}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {InvoiceData.clientData.state || ""}{" "}
                    {InvoiceData.clientData.zip || ""}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                    Matter: {InvoiceData.clientData.case}
                  </Typography>
                </Card>

                <Card sx={{ p: 2, bgcolor: theme.palette.background.paper }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: theme.palette.primary.main,
                      mb: 1,
                      fontWeight: 600,
                    }}
                  >
                    Invoice Details
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2">Invoice No:</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {InvoiceData?.billData?.invoice_num}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2">Issue Date:</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {InvoiceData.billData.issue_date}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2">Due Date:</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {InvoiceData.billData.due_date}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2">Status:</Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color:
                          InvoiceData.billData.status === "paid"
                            ? "success.main"
                            : "warning.main",
                        textTransform: "capitalize",
                      }}
                    >
                      {InvoiceData.billData.status}
                    </Typography>
                  </Box>
                </Card>
              </Box>

              {/* Modern Tables with enhanced styling */}
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "2rem" }}
              >
                <Card sx={{ p: 2 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: theme.palette.primary.main,
                      mb: 1,
                      fontWeight: 600,
                    }}
                  >
                    Summary of the invoice
                  </Typography>
                  {renderSummaryTable()}
                </Card>

                {/* Statement table */}
                <Card sx={{ p: 2 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: theme.palette.primary.main,
                      mb: 1,
                      fontWeight: 600,
                    }}
                  >
                    Detailed Statement of Account
                  </Typography>
                  {renderAccountTable()}
                </Card>
              </Box>

              <Box
                sx={{
                  mt: 4,
                  p: 2,
                  bgcolor: theme.palette.background.default,
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontStyle: "italic", textAlign: "center" }}
                >
                  Please pay within 30 days.
                </Typography>
              </Box>
            </>
          )}
          {selectedLayout === 2 && (
            <>
              {/* Header with Logo */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 2,
                  pb: 1,
                  borderBottom: `2px solid ${theme.palette.primary.main}`,
                }}
              >
                <Avatar
                  sx={{
                    width: 60,
                    height: 60,
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    fontSize: "2rem",
                    mr: 3,
                  }}
                >
                  {InvoiceData.firmData.name?.charAt(0) || "L"}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.primary.main,
                      mb: 1,
                    }}
                  >
                    Law office of {InvoiceData.firmData.name}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {InvoiceData.firmData.address}, {InvoiceData.firmData.city}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {InvoiceData.firmData.state}-{InvoiceData.firmData.zip}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 300, color: theme.palette.primary.main }}
                  >
                    INVOICE
                  </Typography>
                  <Typography variant="h6" sx={{ color: "#666" }}>
                    #{InvoiceData?.billData?.invoice_num}
                  </Typography>
                </Box>
              </Box>

              {/* Client and Invoice Info in Cards */}
              <Box sx={{ display: "flex", gap: 3, mb: 4 }}>
                <Card sx={{ flex: 1, p: 3, boxShadow: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Avatar
                      sx={{
                        bgcolor: theme.palette.success.main,
                        mr: 2,
                        width: 32,
                        height: 32,
                      }}
                    >
                      {InvoiceData.clientData.name?.charAt(0) || "C"}
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Bill To
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 500, mb: 1, textTransform: "capitalize" }}
                  >
                    {InvoiceData.clientData.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    {InvoiceData.clientData.address
                      ? `${InvoiceData.clientData.address}, ${InvoiceData.clientData.city}`
                      : ""}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {InvoiceData.clientData.state || ""}{" "}
                    {InvoiceData.clientData.zip || ""}
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: theme.palette.grey[100],
                      p: 1,
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Matter: {InvoiceData.clientData.case}
                    </Typography>
                  </Box>
                </Card>

                <Card sx={{ flex: 1, p: 3, boxShadow: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Invoice Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Issue Date
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {InvoiceData.billData.issue_date}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Due Date
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {InvoiceData.billData.due_date}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: theme.palette.action.hover,
                      borderRadius: 1,
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Status
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color:
                          InvoiceData.billData.status === "paid"
                            ? "success.main"
                            : "warning.main",
                        textTransform: "uppercase",
                      }}
                    >
                      {InvoiceData.billData.status}
                    </Typography>
                  </Box>
                </Card>
              </Box>

              {/* Enhanced Tables with Logo styling */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <Card sx={{ p: 3, boxShadow: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Box
                      sx={{
                        width: 4,
                        height: 24,
                        bgcolor: theme.palette.primary.main,
                        mr: 2,
                      }}
                    />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Summary of the invoice
                    </Typography>
                  </Box>
                  {renderSummaryTable()}
                </Card>

                <Card sx={{ p: 3, boxShadow: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Box
                      sx={{
                        width: 4,
                        height: 24,
                        bgcolor: theme.palette.primary.main,
                        mr: 2,
                      }}
                    />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Detailed Statement of Account
                    </Typography>
                  </Box>
                  {renderAccountTable()}
                </Card>
              </Box>

              <Box
                sx={{
                  mt: 4,
                  textAlign: "center",
                  py: 3,
                  bgcolor: theme.palette.background.default,
                  borderRadius: 2,
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  Thank you for your business!
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Please pay within 30 days.
                </Typography>
              </Box>
            </>
          )}
          {selectedLayout === 3 && (
            <>
              {/* Professional Header with Gradient */}
              <Box
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  color: "white",
                  p: 3,
                  mb: 3,
                  borderRadius: 2,
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    right: 0,
                    width: "100px",
                    height: "100%",
                    background: "rgba(255,255,255,0.1)",
                    transform: "skewX(-15deg)",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                      INVOICE
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.9 }}>
                      Law office of {InvoiceData.firmData.name}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      {InvoiceData.firmData.address},{" "}
                      {InvoiceData.firmData.city}, {InvoiceData.firmData.state}-
                      {InvoiceData.firmData.zip}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Chip
                      label={`#${InvoiceData?.billData?.invoice_num}`}
                      sx={{
                        bgcolor: "rgba(255,255,255,0.2)",
                        color: "white",
                        fontSize: "1rem",
                        fontWeight: 600,
                        mb: 1,
                      }}
                    />
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {InvoiceData.billData.issue_date}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Client and Invoice Details in Enhanced Cards */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1fr",
                  gap: 3,
                  mb: 4,
                }}
              >
                {/* Client Information Card */}
                <Card
                  sx={{
                    p: 3,
                    borderLeft: `4px solid ${theme.palette.primary.main}`,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                    transition: "transform 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Avatar
                      sx={{
                        bgcolor: theme.palette.primary.main,
                        mr: 2,
                        width: 40,
                        height: 40,
                        fontSize: "1.2rem",
                      }}
                    >
                      {InvoiceData.clientData.name?.charAt(0) || "C"}
                    </Avatar>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                      }}
                    >
                      Bill To
                    </Typography>
                  </Box>
                  <Box sx={{ pl: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        textTransform: "capitalize",
                      }}
                    >
                      {InvoiceData.clientData.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", mb: 0.5 }}
                    >
                      {InvoiceData.clientData.address
                        ? `${InvoiceData.clientData.address}, ${InvoiceData.clientData.city}`
                        : ""}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", mb: 2 }}
                    >
                      {InvoiceData.clientData.state || ""}{" "}
                      {InvoiceData.clientData.zip || ""}
                    </Typography>
                    <Box
                      sx={{
                        bgcolor: theme.palette.action.hover,
                        p: 2,
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", display: "block" }}
                      >
                        Matter Number
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {InvoiceData.clientData.case}
                      </Typography>
                    </Box>
                  </Box>
                </Card>

                {/* Invoice Summary Card */}
                <Card
                  sx={{
                    p: 3,
                    bgcolor: theme.palette.grey[50],
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      mb: 3,
                      color: theme.palette.primary.main,
                    }}
                  >
                    Invoice Details
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      Issue Date
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {InvoiceData.billData.issue_date}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      Due Date
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {InvoiceData.billData.due_date}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      Status
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={InvoiceData.billData.status}
                        color={
                          InvoiceData.billData.status === "paid"
                            ? "success"
                            : InvoiceData.billData.status === "pending"
                            ? "warning"
                            : InvoiceData.billData.status === "unpaid"
                            ? "error"
                            : "primary"
                        }
                        size="small"
                        sx={{ textTransform: "capitalize", fontWeight: 600 }}
                      />
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      Total Amount
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.primary.main,
                        mt: 0.5,
                      }}
                    >
                      $
                      {parseFloat(
                        InvoiceData.billData.total_amount || 0
                      ).toFixed(2)}
                    </Typography>
                  </Box>
                </Card>
              </Box>

              {/* Enhanced Tables with Professional Styling */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {/* Summary Table */}
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 2,
                      pb: 1,
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 24,
                        bgcolor: theme.palette.primary.main,
                        mr: 2,
                        borderRadius: 1,
                      }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                      }}
                    >
                      Summary of the Invoice
                    </Typography>
                  </Box>
                  <Card
                    sx={{
                      overflow: "hidden",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    {renderSummaryTable()}
                  </Card>
                </Box>

                {/* Account Statement Table */}
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 2,
                      pb: 1,
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 24,
                        bgcolor: theme.palette.primary.main,
                        mr: 2,
                        borderRadius: 1,
                      }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                      }}
                    >
                      Detailed Statement of Account
                    </Typography>
                  </Box>
                  <Card
                    sx={{
                      overflow: "hidden",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    {renderAccountTable()}
                  </Card>
                </Box>
              </Box>

              {/* Professional Footer */}
              <Box
                sx={{
                  mt: 5,
                  p: 3,
                  bgcolor: theme.palette.grey[50],
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  textAlign: "center",
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 60,
                    height: 4,
                    bgcolor: theme.palette.primary.main,
                    borderRadius: "0 0 4px 4px",
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                    color: theme.palette.primary.main,
                  }}
                >
                  Thank You for Your Business!
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "text.secondary", mb: 2 }}
                >
                  Please pay within 30 days of the invoice date.
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontStyle: "italic", color: "text.secondary" }}
                >
                  For questions regarding this invoice, please contact us.
                </Typography>
              </Box>
            </>
          )}
        </Card>
      </Box>
      <Box
        sx={{
          height: "fit-content",
          borderTop: "1px solid #ddd",
          position: "sticky",
          bottom: 0,
          backdropFilter: "blur(10px)",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          p: "0.5rem",
          zIndex: 1000,
          gap: "0.5rem",
          bgcolor: "rgba(255, 255, 255, 0.8)",
        }}
      >
        <Button
          variant="text"
          size="small"
          onClick={() => setUseExtra((prev) => !prev)}
        >
          {useExtra ? "Show Original" : "Show Updated"}
        </Button>
        <Button variant="outlined" size="small" onClick={closeDialog}>
          Cancel
        </Button>
        <LoadingButton
          // loading={isSubmitting}
          variant="contained"
          color="primary"
          onClick={handleDownloadPDF}
          size="small"
        >
          Download
        </LoadingButton>
      </Box>
    </>
  );
};

export default BillPreview;
